import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API, useOrg } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Boxes, AlertTriangle, Edit2 } from "lucide-react";

const InventoryPage = () => {
  const { currentOrg } = useOrg();
  const [inventory, setInventory] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    quantity_in_hand: "",
    reorder_level: "",
    location: ""
  });

  const fetchInventory = async () => {
    if (!currentOrg) return;
    try {
      const [invRes, lowRes] = await Promise.all([
        axios.get(`${API}/inventory`, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        }),
        axios.get(`${API}/inventory/low-stock`, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        })
      ]);
      setInventory(invRes.data);
      setLowStock(lowRes.data);
    } catch (error) {
      console.error("Failed to fetch inventory:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
  }, [currentOrg]);

  const handleEdit = (inv) => {
    setEditingItem(inv);
    setForm({
      quantity_in_hand: inv.quantity_in_hand?.toString() || "0",
      reorder_level: inv.reorder_level?.toString() || "0",
      location: inv.location || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/inventory/${editingItem.item_id}`, {
        item_id: editingItem.item_id,
        quantity_in_hand: parseInt(form.quantity_in_hand),
        reorder_level: parseInt(form.reorder_level),
        location: form.location
      }, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      toast.success("Inventory updated");
      await fetchInventory();
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div>
        <h1 className="font-outfit text-2xl font-medium text-zinc-900">Inventory</h1>
        <p className="text-muted-foreground text-sm">Track stock levels for your products</p>
      </div>

      {/* Low Stock Alert */}
      {lowStock.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 p-4">
          <div className="flex items-center gap-2 text-amber-800 mb-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">Low Stock Alert</span>
          </div>
          <p className="text-sm text-amber-700">
            {lowStock.length} item(s) are below reorder level
          </p>
        </div>
      )}

      {/* Inventory Table */}
      <div className="bg-white border border-zinc-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : inventory.length === 0 ? (
          <div className="p-16 text-center">
            <Boxes className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-muted-foreground">No inventory items</p>
            <p className="text-sm text-muted-foreground mt-2">
              Add product items to start tracking inventory
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Item</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">In Stock</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Reorder Level</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Location</th>
                  <th className="text-center py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Status</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {inventory.map((inv) => {
                  const isLow = inv.quantity_in_hand <= inv.reorder_level;
                  return (
                    <tr key={inv.inventory_id} className={`border-b border-zinc-100 hover:bg-zinc-50 ${isLow ? 'bg-amber-50/50' : ''}`}>
                      <td className="py-3 px-4">
                        <p className="font-medium">{inv.item?.name || 'Unknown Item'}</p>
                        {inv.item?.upc && <p className="text-xs text-muted-foreground">UPC: {inv.item.upc}</p>}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-medium ${isLow ? 'text-amber-600' : ''}`}>
                          {inv.quantity_in_hand}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-muted-foreground">{inv.reorder_level}</td>
                      <td className="py-3 px-4 text-muted-foreground">{inv.location || '-'}</td>
                      <td className="py-3 px-4 text-center">
                        {isLow ? (
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700">
                            Low Stock
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-emerald-50 text-emerald-700">
                            In Stock
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(inv)}
                          data-testid={`edit-inventory-${inv.item_id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Inventory</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="quantity">Quantity in Hand</Label>
              <Input
                id="quantity"
                type="number"
                value={form.quantity_in_hand}
                onChange={(e) => setForm({...form, quantity_in_hand: e.target.value})}
                data-testid="inventory-quantity-input"
              />
            </div>
            <div>
              <Label htmlFor="reorder">Reorder Level</Label>
              <Input
                id="reorder"
                type="number"
                value={form.reorder_level}
                onChange={(e) => setForm({...form, reorder_level: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={form.location}
                onChange={(e) => setForm({...form, location: e.target.value})}
                placeholder="e.g., Warehouse A"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="inventory-submit-btn">Update</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryPage;
